/**
 * web/src/hooks/mutations/useSendNudgeNow.ts
 * Manual "Send now" for one planned nudge entry (P2 nudge substrate). The
 * server skips the dueAt gate + kill switch but keeps every other check —
 * a 409 means the nudge is no longer sendable (already sent/cancelled, the
 * thread got a reply, nothing outstanding, or the send itself failed).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { sendNudgeNow } from '../../api/tracked-emails';
import { toastApiError } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

export interface UseSendNudgeNowVars {
  trackedEmailId: string;
  /** Zero-based position of the entry in the plan. */
  index: number;
  /** Skip ONLY the inbound-activity gate ("Send anyway" after seeing the
   *  evidence banner). Every other check still runs. */
  force?: boolean;
}

/**
 * Fires one planned nudge entry immediately.
 *
 * @returns React Query mutation handle (variables: { trackedEmailId, index })
 * @sideEffects HTTP POST → one Gmail send; invalidates speakers +
 *              tracked-emails caches
 * @idempotent no — a second call 409s against the now-sent entry
 */
export function useSendNudgeNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: UseSendNudgeNowVars) =>
      sendNudgeNow(vars.trackedEmailId, vars.index, vars.force ?? false),
    onSuccess: () => {
      toast.success('Nudge sent');
    },
    onError: (err) => {
      toastApiError(err, 'Could not send the nudge');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.speakers.root() });
      qc.invalidateQueries({ queryKey: qk.trackedEmails.root() });
    },
  });
}
